// LazyErrorBoundary — оборачивает React.lazy + Suspense чанки.
//
// Сценарий 1: пользователь offline → `import()` для lazy-чанка проваливается с
// TypeError. Без boundary это вылетает в корневой router error handler с белым
// экраном. Boundary показывает мини-Alert и кнопку «Обновить страницу».
//
// Сценарий 2 (hotfix Этапа 4): boundary поймал ошибку рендера на одной странице
// (например, /admin/logs), пользователь перешёл на другую (`/admin/users`). В
// обычном React Error Boundary state.error НЕ сбрасывается на route change —
// boundary продолжает показывать «ошибка» даже после смены URL. Лечится:
// внутренний wrapper подписывается на useLocation() и при смене pathname'а
// явно вызывает reset() через ref.
//
// См. critic Этапа 4 BP002.
import { Component, type ReactNode, useEffect, useRef } from "react";
import { Button, Result } from "antd";
import { useLocation } from "react-router-dom";

interface InnerProps {
  children: ReactNode;
  /** Передаётся из обёртки, чтобы родитель мог вызвать reset(). */
  registerReset: (resetFn: () => void) => void;
}

interface State {
  error: Error | null;
}

class InnerBoundary extends Component<InnerProps, State> {
  state: State = { error: null };

  componentDidMount() {
    this.props.registerReset(this.reset);
  }

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error) {
    // eslint-disable-next-line no-console
    console.error("LazyErrorBoundary caught:", error);
  }

  reset = () => {
    if (this.state.error !== null) {
      this.setState({ error: null });
    }
  };

  render() {
    if (this.state.error) {
      return (
        <Result
          status="warning"
          title="Не удалось загрузить страницу"
          subTitle="Проверьте интернет-соединение и обновите страницу."
          extra={
            <Button type="primary" onClick={() => window.location.reload()}>
              Обновить
            </Button>
          }
        />
      );
    }
    return this.props.children;
  }
}

interface Props {
  children: ReactNode;
}

export function LazyErrorBoundary({ children }: Props) {
  const location = useLocation();
  const resetRef = useRef<(() => void) | null>(null);

  // На смену pathname'а сбрасываем boundary — иначе ошибка с предыдущей страницы
  // «залипает» на новой даже после смены URL.
  useEffect(() => {
    resetRef.current?.();
  }, [location.pathname]);

  return (
    <InnerBoundary
      registerReset={(fn) => {
        resetRef.current = fn;
      }}
    >
      {children}
    </InnerBoundary>
  );
}
